import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dropdown,
  Input,
  Select,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import { API } from '@/services/api.service';
import RecipientsFields from './RecipientsFields';
import { fileTitle } from '../format';

const { Text } = Typography;

// Starting points for a new row; every field stays editable.
const PRESETS = [
  {
    key: 'tbYtd',
    type: 'analyticTrialBalance',
    period: 'ytd',
    title: 'Analiticki bruto bilans',
  },
  {
    key: 'tbMonth',
    type: 'analyticTrialBalance',
    period: 'month',
    title: 'Bruto bilans za mesec',
  },
  {
    key: 'journal',
    type: 'journal',
    title: 'Dnevnik knjizenja',
    includeOperator: true,
  },
  {
    key: 'balanceSheet',
    type: 'generalLedger',
    period: 'ytd',
    accountFrom: '0',
    accountTo: '4',
    title: 'Bilans stanja',
  },
  {
    key: 'incomeStatement',
    type: 'generalLedger',
    period: 'month',
    accountFrom: '5',
    accountTo: '6',
    title: 'Bilans uspeha',
  },
  {
    key: 'glYtd',
    type: 'generalLedger',
    period: 'ytd',
    accountFrom: '0',
    accountTo: '9',
    title: 'General ledger',
  },
  { key: 'vat', type: 'vatBySupplier', title: 'General ledger' },
  {
    key: 'suppliers',
    type: 'supplierTurnover',
    account: '435,436',
    title: 'General ledger',
  },
  {
    key: 'depreciation',
    type: 'depreciation',
    title: 'Predracun amortizacije',
  },
  { key: 'cashFlow', type: 'cashFlow', title: 'Cash flow' },
  {
    key: 'investments',
    type: 'investments',
    title: 'Osnovna sredstva-investicije',
  },
  { key: 'fccs', type: 'fccs', title: 'FCCS' },
  {
    key: 'parentIncomeStatement',
    type: 'parentIncomeStatement',
    title: 'INCOME STATEMENT',
  },
  {
    key: 'groupReport',
    type: 'groupReport',
    title: 'Orbit Serbia New PL Template',
  },
  {
    key: 'monthlyResult',
    type: 'monthlyResult',
    title: 'Mesecni rezultat',
    format: 'xlsx',
    secondCurrency: 'EUR',
    language: '',
    belowOperating: true,
    // New items start short and without markup; the accountant sets both.
    variant: 'short',
    markup: 'none',
  },
  {
    key: 'monthlyResultPdf',
    type: 'monthlyResult',
    title: 'Mesecni rezultat PDF',
    format: 'pdf',
    secondCurrency: 'EUR',
    language: '',
    belowOperating: true,
    variant: 'short',
    markup: 'none',
  },
  // One PDF per partner on the month's last day, as a ZIP.
  { key: 'ios', type: 'ios', title: 'IOS', account: '', language: '' },
];

// Second currency of the monthly result, next to RSD; '' = dinars only.
const SECOND_CURRENCIES = ['EUR', 'USD', 'RUB', 'GBP', 'CHF'];

// Monthly result: short (operating income, expenses, result) or the full APR
// form; which markups the client sees (in the file, on screen and in the mail).
const MONTHLY_VARIANTS = ['short', 'full'];
const MONTHLY_MARKUPS = ['none', 'mu1', 'mu2', 'both'];

/**
 * What the client gets every month: account-name language, the currency of
 * the extra columns, and the list of reports. Reports with the same file name
 * share one workbook, as sheets in list order.
 * Recipients outside the platform get the files set for them attached to an
 * email on every publication; HLB copy recipients are in cc on every mail.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {Function} props.onSaved - Called after saving
 * @returns {JSX.Element} The settings editor
 */
export default function PackSettings({ companyId, onSaved }) {
  const { t } = useTranslation('clientReports');
  const queryClient = useQueryClient();
  const [language, setLanguage] = useState('sr');
  const [fxCurrency, setFxCurrency] = useState('');
  const [reports, setReports] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [copies, setCopies] = useState([]);
  const [notifyClientUsers, setNotifyClientUsers] = useState(true);
  const [dirty, setDirty] = useState(false);

  const { data } = useQuery({
    queryKey: ['client-reports-pack-profile', companyId],
    queryFn: () => API.clientReports.getReportProfile(companyId),
  });

  useEffect(() => {
    if (!data?.data) return;
    setLanguage(data.data.language);
    setFxCurrency(data.data.fxCurrency || '');
    setReports(data.data.reports || []);
    setRecipients(data.data.externalRecipients || []);
    setCopies(data.data.copyRecipients || []);
    setNotifyClientUsers(data.data.notifyClientUsers !== false);
    setDirty(false);
  }, [data]);

  const change = (setter) => (value) => {
    setter(value);
    setDirty(true);
  };
  const updateRow = (index, patch) =>
    change(setReports)(
      reports.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  const fileOptions = [...new Set(reports.map(fileTitle))].map((title) => ({
    value: title,
    label: title,
  }));

  const moveRow = (index, delta) => {
    const next = [...reports];
    const [row] = next.splice(index, 1);
    next.splice(index + delta, 0, row);
    change(setReports)(next);
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      API.clientReports.saveReportProfile(companyId, {
        language,
        fxCurrency,
        reports,
        externalRecipients: recipients,
        copyRecipients: copies,
        notifyClientUsers,
      }),
    onSuccess: () => {
      setDirty(false);
      queryClient.invalidateQueries({
        queryKey: ['client-reports-pack-profile', companyId],
      });
      message.success(t('pack.saved'));
      onSaved?.();
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  const columns = [
    {
      title: t('pack.fileName'),
      key: 'title',
      width: 220,
      render: (_, r, i) => (
        <Input
          value={r.title}
          onChange={(e) => updateRow(i, { title: e.target.value })}
        />
      ),
    },
    {
      title: t('pack.report'),
      key: 'type',
      width: 220,
      render: (_, r) => <Text>{t(`pack.types.${r.type}`)}</Text>,
    },
    {
      title: t('pack.options'),
      key: 'options',
      render: (_, r, i) => (
        <Space wrap>
          {['analyticTrialBalance', 'generalLedger'].includes(r.type) && (
            <Select
              style={{ width: 170 }}
              value={r.period}
              onChange={(period) => updateRow(i, { period })}
              options={['ytd', 'month'].map((p) => ({
                value: p,
                label: t(`pack.period.${p}`),
              }))}
            />
          )}
          {r.type === 'generalLedger' && (
            <>
              <Text type="secondary">{t('pack.accounts')}</Text>
              <Input
                style={{ width: 56 }}
                value={r.accountFrom}
                onChange={(e) => updateRow(i, { accountFrom: e.target.value })}
              />
              –
              <Input
                style={{ width: 56 }}
                value={r.accountTo}
                onChange={(e) => updateRow(i, { accountTo: e.target.value })}
              />
            </>
          )}
          {r.type === 'journal' && (
            <Space size={4}>
              <Switch
                size="small"
                checked={r.includeOperator !== false}
                onChange={(includeOperator) =>
                  updateRow(i, { includeOperator })
                }
              />
              <Text type="secondary">{t('pack.includeOperator')}</Text>
            </Space>
          )}
          {r.type === 'monthlyResult' && (
            <>
              <Select
                style={{ width: 90 }}
                value={r.format || 'xlsx'}
                onChange={(format) => updateRow(i, { format })}
                options={[
                  { value: 'xlsx', label: 'Excel' },
                  { value: 'pdf', label: 'PDF' },
                ]}
              />
              <Text type="secondary">{t('monthlyResult.secondCurrency')}</Text>
              <Select
                style={{ width: 120 }}
                value={r.secondCurrency ?? 'EUR'}
                onChange={(secondCurrency) => updateRow(i, { secondCurrency })}
                options={[
                  { value: '', label: t('monthlyResult.rsdOnly') },
                  ...SECOND_CURRENCIES.map((c) => ({ value: c, label: c })),
                ]}
              />
              <Select
                style={{ width: 150 }}
                value={r.language || ''}
                onChange={(lang) => updateRow(i, { language: lang })}
                options={[
                  { value: '', label: t('monthlyResult.packLanguage') },
                  { value: 'sr', label: t('pack.languageSr') },
                  { value: 'en', label: t('pack.languageEn') },
                ]}
              />
              {/* An item saved before the short report existed is full. */}
              <Text type="secondary">{t('monthlyShort.variant')}</Text>
              <Select
                style={{ width: 250 }}
                value={r.variant === 'short' ? 'short' : 'full'}
                onChange={(variant) => updateRow(i, { variant })}
                options={MONTHLY_VARIANTS.map((v) => ({
                  value: v,
                  label: t(`monthlyShort.variants.${v}`),
                }))}
              />
              <Text type="secondary">{t('monthlyShort.markup')}</Text>
              <Select
                style={{ width: 250 }}
                value={MONTHLY_MARKUPS.includes(r.markup) ? r.markup : 'none'}
                onChange={(markup) => updateRow(i, { markup })}
                options={MONTHLY_MARKUPS.map((m) => ({
                  value: m,
                  label: t(`monthlyShort.markups.${m}`),
                }))}
              />
              {r.variant !== 'short' && (
                <Space size={4}>
                  <Switch
                    size="small"
                    checked={r.belowOperating !== false}
                    onChange={(belowOperating) =>
                      updateRow(i, { belowOperating })
                    }
                  />
                  <Text type="secondary">
                    {t('monthlyResult.belowOperating')}
                  </Text>
                </Space>
              )}
            </>
          )}
          {r.type === 'ios' && (
            <>
              <Text type="secondary">{t('pack.iosAccounts')}</Text>
              <Input
                style={{ width: 160 }}
                placeholder="435000"
                value={r.account}
                onChange={(e) => updateRow(i, { account: e.target.value })}
              />
              <Select
                style={{ width: 190 }}
                value={r.language || ''}
                onChange={(lang) => updateRow(i, { language: lang })}
                options={[
                  { value: '', label: t('ios.languageAuto') },
                  ...['sr', 'en', 'ru', 'zh'].map((l) => ({
                    value: l,
                    label: t(`ios.languages.${l}`),
                  })),
                ]}
              />
            </>
          )}
          {r.type === 'supplierTurnover' && (
            <>
              <Text type="secondary">{t('pack.supplierAccounts')}</Text>
              <Input
                style={{ width: 110 }}
                value={r.account}
                onChange={(e) => updateRow(i, { account: e.target.value })}
              />
            </>
          )}
        </Space>
      ),
    },
    {
      key: 'actions',
      width: 110,
      render: (_, __, i) => (
        <Space size={4}>
          <Button
            size="small"
            shape="circle"
            icon={<ArrowUpOutlined />}
            disabled={i === 0}
            onClick={() => moveRow(i, -1)}
          />
          <Button
            size="small"
            shape="circle"
            icon={<ArrowDownOutlined />}
            disabled={i === reports.length - 1}
            onClick={() => moveRow(i, 1)}
          />
          <Button
            size="small"
            shape="circle"
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              change(setReports)(reports.filter((_, k) => k !== i))
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ display: 'flex', marginBottom: 12 }}>
        <Text>{t('pack.language')}</Text>
        <Select
          style={{ width: 150 }}
          value={language}
          onChange={change(setLanguage)}
          options={[
            { value: 'sr', label: t('pack.languageSr') },
            { value: 'en', label: t('pack.languageEn') },
          ]}
        />
        <Text>{t('pack.fxCurrency')}</Text>
        <Select
          style={{ width: 130 }}
          value={fxCurrency}
          onChange={change(setFxCurrency)}
          options={[
            { value: '', label: t('pack.fxNone') },
            { value: 'USD', label: 'USD' },
            { value: 'EUR', label: 'EUR' },
            { value: 'GBP', label: 'GBP' },
            { value: 'CHF', label: 'CHF' },
          ]}
        />
      </Space>

      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
        {t('pack.sameFileHint')}
      </div>
      <Table
        scroll={{ x: 'max-content' }}
        rowKey={(_, i) => i}
        size="small"
        pagination={false}
        columns={columns}
        dataSource={reports}
      />

      <div style={{ marginTop: 8 }}>
        <Dropdown
          menu={{
            items: PRESETS.map((p) => ({
              key: p.key,
              label: t(`pack.presets.${p.key}`),
            })),
            onClick: ({ key }) => {
              const preset = { ...PRESETS.find((p) => p.key === key) };
              delete preset.key;
              change(setReports)([
                ...reports,
                {
                  period: 'ytd',
                  accountFrom: '0',
                  accountTo: '9',
                  includeOperator: true,
                  account: '435',
                  ...preset,
                },
              ]);
            },
          }}
        >
          <Button shape="round" icon={<PlusOutlined />}>
            {t('pack.add')}
          </Button>
        </Dropdown>
      </div>

      <div style={{ marginTop: 16 }}>
        <RecipientsFields
          companyId={companyId}
          recipients={recipients}
          onRecipientsChange={change(setRecipients)}
          copies={copies}
          onCopiesChange={change(setCopies)}
          notifyClientUsers={notifyClientUsers}
          onNotifyChange={change(setNotifyClientUsers)}
          fileOptions={fileOptions}
        />
      </div>

      <Space style={{ marginTop: 16, display: 'flex' }}>
        <Button
          type="primary"
          shape="round"
          className="btn-primary"
          disabled={!dirty}
          loading={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {t('pack.save')}
        </Button>
        {dirty && <Text type="warning">{t('fccs.unsaved')}</Text>}
      </Space>
    </div>
  );
}
